<template>
	<span
		v-if="props.lazy"
		ref="root"
		class="inline-flex"
		@focusin="activate"
		@focusout="deactivate"
		@touchstart.passive="activate"
		@touchend.passive="deactivate"
	>
		<v-tooltip
			v-if="activated"
			v-model:shown="shown"
			:triggers="[]"
			popper-class="basic-tooltip"
			:disabled="props.disabled"
			:theme="props.theme"
		>
			<slot />
			<template #popper>
				<slot name="tooltip" />
			</template>
		</v-tooltip>
		<slot v-else />
	</span>
	<v-tooltip
		v-else
		popper-class="basic-tooltip"
		:disabled="props.disabled"
		:theme="props.theme"
	>
		<slot />
		<template #popper>
			<slot name="tooltip" />
		</template>
	</v-tooltip>
</template>

<script setup lang="ts">
import { useElementHover, useEventListener } from '@vueuse/core';

type Props = {
	disabled?: boolean;
	theme?: 'primary';
	lazy?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
	theme: undefined,
});

const root = useTemplateRef<HTMLElement>('root');
const isHovered = useElementHover(root, { triggerOnRemoval: true });

const activated = ref(false);
const shown = ref(false);

let activateGeneration = 0;

async function activate () {
	if (!props.lazy || props.disabled) {
		return;
	}
	const generation = ++activateGeneration;
	if (!activated.value) {
		activated.value = true;
		await nextTick();
	}
	if (generation !== activateGeneration) {
		shown.value = false;
		return;
	}
	shown.value = true;
}

function deactivate () {
	activateGeneration++;
	shown.value = false;
}

watch(isHovered, (hovered) => {
	if (!props.lazy || props.disabled) {
		return;
	}
	if (hovered) {
		activate();
	}
	else {
		deactivate();
	}
});

useEventListener(
	() => (shown.value ? window : undefined),
	'scroll',
	deactivate,
	{ capture: true, passive: true },
);

useEventListener(
	() => (shown.value ? window : undefined),
	'pointermove',
	(event: PointerEvent) => {
		const el = root.value;
		if (el == null) {
			return;
		}
		if (el.matches(':hover')) {
			return;
		}
		const hit = document.elementFromPoint(event.clientX, event.clientY);
		if (hit != null && el.contains(hit)) {
			return;
		}
		isHovered.value = false;
	},
	{ passive: true },
);
</script>

<style lang="scss">
.v-popper__popper.basic-tooltip {
	pointer-events: none;

	.v-popper__inner {
		p,
		ul,
		ol,
		li,
		h1,
		h2,
		h3,
		h4,
		h5,
		h6,
		strong,
		em,
		div {
			// reset some global styles to make <p>, <strong>, ... usable in <template #tooltip>
			margin: 0;
			padding: 0;
			color: inherit;
			font-size: inherit;
			line-height: 1.5;
		}
	}
}

.v-popper__popper.v-popper--theme-primary {
	.v-popper__inner {
		@apply bg-primary text-white text-xxs font-medium;
	}
	.v-popper__arrow-container {
		@apply hidden;
	}
}
</style>
